import { useState } from 'react';
import { toast } from 'sonner';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import {
  BulkAction,
  BulkActionContext,
} from '@/components/table/types/BulkAction.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';

interface BulkActionRunner<T extends RowWithId> {
  /** Id of the action in flight, or `null` — drives the spinner and the lock. */
  runningId: string | null;
  runAction: (action: BulkAction<T>) => Promise<void>;
}

/**
 * Shared mechanics around a bulk action: confirmation, per-action loading
 * state, error toast and clearing the selection once it resolves. Declaring
 * an action stays a matter of data — see {@link BulkAction}.
 */
export const useBulkActionRunner = <T extends RowWithId>(
  rows: T[],
  ctx: BulkActionContext,
): BulkActionRunner<T> => {
  const { showDialog } = confirmDialogUseHook();
  const [runningId, setRunningId] = useState<string | null>(null);

  const runAction = async (action: BulkAction<T>) => {
    if (action.disabled?.(rows)) return;

    if (action.confirm) {
      const confirmed = await showDialog(action.confirm(rows));
      if (!confirmed) return;
    }

    try {
      setRunningId(action.id);
      await action.onClick(rows, ctx);
      if (!action.keepSelectionOnComplete) ctx.clearSelection();
    } catch (error) {
      toast.error('Nie udało się wykonać akcji', {
        description: (error as Error).message,
      });
    } finally {
      setRunningId(null);
    }
  };

  return { runningId, runAction };
};
